import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import HistoryIcon from '@mui/icons-material/History';
import { useSnackbar } from '../../../utils/useSnackbar';
import { setActivePage } from '../../../features/navigation/navigationSlice';
import { getOrgsWithBalances } from '../../../utils/api/superAdmin_apis';
import { getUserSummary, getUserHistory } from '../../../utils/api/usageLog_apis';
import { STATUS } from '../../../utils/api/sharedfunction';
import PageHeader from '../../Header/PageHeader';
import ButtonComponent from '../../customButton';
import TableComponent from '../../TableComponent';
import { renderTableCell } from '../../../utils/commonfunctions';
import styles from '../../../styles/component.module.scss';
import usageToolbar from '../../../styles/admin/usageByUserToolbar.module.scss';
import pageStyles from '../../../styles/admin/usageByUser.module.scss';
import { FormComponent } from '../../FormComponent';

const SECTION_LABELS = {
  script_creator: 'Script Creator',
  test_launch: 'Test Launch',
  script_manager: 'Script Manager',
};

const BALANCE_CARD_CONFIG = {
  Credits: {
    icon: DescriptionOutlinedIcon,
    accent: '#2563eb',
    bgTint: 'rgba(37, 99, 235, 0.06)',
  },
};

function formatDate(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return d.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const UsageByUser = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const showSnakbarRef = useRef(showSnackbar);
  showSnakbarRef.current = showSnackbar;
  const activePage = useSelector((state) => state.navigation.activePage);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [summaryData, setSummaryData] = useState({
    orgRemaining: {},
    orgTotals: {},
    userSummary: [],
  });
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyUser, setHistoryUser] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyPaginationModel, setHistoryPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [historyPagination, setHistoryPagination] = useState({ page: 1, pageSize: 25, total: 0 });
  const lastHistoryFetchKeyRef = useRef('');

  useEffect(() => {
    dispatch(setActivePage('Usage by User'));
  }, [dispatch]);

  const loadOrgs = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const response = await getOrgsWithBalances();
      if (response.status === STATUS.SUCCESS) {
        setOrgs(response.data || []);
        if (!selectedOrgId && response.data?.length) {
          setSelectedOrgId(response.data[0].orgId);
        }
      } else {
        showSnakbarRef.current(response.error || 'Failed to load organizations', 'error');
      }
    } catch (err) {
      showSnakbarRef.current(err?.message || 'Failed to load organizations', 'error');
    } finally {
      setLoadingOrgs(false);
    }
  }, [selectedOrgId]);

  const loadSummary = useCallback(async () => {
    if (!selectedOrgId) {
      setSummaryData({ orgRemaining: {}, orgTotals: {}, userSummary: [] });
      return;
    }
    setLoadingSummary(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await getUserSummary(selectedOrgId, params);
      if (response.status === STATUS.SUCCESS) {
        setSummaryData(response.data || { orgRemaining: {}, orgTotals: {}, userSummary: [] });
      } else {
        showSnakbarRef.current(response.error || 'Failed to load user summary', 'error');
      }
    } catch (err) {
      showSnakbarRef.current(err?.message || 'Failed to load user summary', 'error');
    } finally {
      setLoadingSummary(false);
    }
  }, [selectedOrgId, startDate, endDate]);

  const fetchUserHistory = useCallback(
    async (user, paginationOverride) => {
      if (!user?.userId || !selectedOrgId) return;
      const page = paginationOverride?.page ?? historyPaginationModel.page;
      const pageSize = paginationOverride?.pageSize ?? historyPaginationModel.pageSize;

      setLoadingHistory(true);
      try {
        const params = {};
        const start = typeof startDate === 'string' ? startDate.trim() : '';
        const end = typeof endDate === 'string' ? endDate.trim() : '';
        if (start) params.startDate = start;
        if (end) params.endDate = end;
        params.page = page + 1;
        params.pageSize = pageSize;

        const response = await getUserHistory(selectedOrgId, user.userId, params);
        if (response.status === STATUS.SUCCESS) {
          const rows = response.data?.rows || [];
          const pagination = response.data?.pagination || { page: 1, pageSize, total: rows.length };
          setHistoryRows(rows);
          setHistoryPagination(pagination);
        } else {
          showSnakbarRef.current(response.error || 'Failed to load history', 'error');
        }
      } catch (err) {
        showSnakbarRef.current(err?.message || 'Failed to load history', 'error');
      } finally {
        setLoadingHistory(false);
      }
    },
    [selectedOrgId, startDate, endDate]
  );

  useEffect(() => {
    loadOrgs();
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Re-fetch history when modal opens or date range changes. Ref ensures we only fetch once per key (no loop).
  const historyUserId = historyUser?.userId ?? null;
  useEffect(() => {
    if (!historyModalOpen || !historyUser) {
      lastHistoryFetchKeyRef.current = '';
      return;
    }
    const key = `${historyUserId}-${startDate}-${endDate}`;
    if (lastHistoryFetchKeyRef.current === key) return;
    lastHistoryFetchKeyRef.current = key;
    fetchUserHistory(historyUser, { page: 0, pageSize: historyPaginationModel.pageSize });
  }, [historyModalOpen, historyUserId, startDate, endDate, fetchUserHistory]);

  // Reset to first page when filters change (org or dates)
  useEffect(() => {
    setHistoryPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [selectedOrgId, startDate, endDate]);

  const handleOrgChange = (e) => {
    setSelectedOrgId(e.target.value || '');
  };

  const handleViewHistory = (user) => {
    setHistoryUser(user);
    setHistoryModalOpen(true);
    setHistoryRows([]);
    setHistoryPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setHistoryUser(null);
    setHistoryRows([]);
  };

  const handleHistoryPaginationModelChange = (model) => {
    const nextPage = model.page !== undefined ? Number(model.page) : historyPaginationModel.page;
    const nextPageSize = model.pageSize !== undefined ? Number(model.pageSize) : historyPaginationModel.pageSize;
    const safePage = Number.isInteger(nextPage) && nextPage >= 0 ? nextPage : 0;
    const safePageSize = Number.isInteger(nextPageSize) && nextPageSize > 0 ? Math.min(nextPageSize, 100) : 25;
    const currPage = Number(historyPaginationModel.page);
    const currPageSize = Number(historyPaginationModel.pageSize);
    if (safePage === currPage && safePageSize === currPageSize) return;
    setHistoryPaginationModel({ page: safePage, pageSize: safePageSize });
    if (historyModalOpen && historyUser) {
      fetchUserHistory(historyUser, { page: safePage, pageSize: safePageSize });
    }
  };
  const handleHistoryPageChange = (event, page) => {
    handleHistoryPaginationModelChange({ page });
  };
  const handleHistoryPageSizeChange = (pageSize) => {
    handleHistoryPaginationModelChange({ page: 0, pageSize });
  };

  const breadcrumbItems = [{ label: 'Settings' }, { label: activePage }];

  const { orgRemaining, orgTotals, userSummary } = summaryData;

  const SummaryCard = ({ label, used, remaining }) => {
    const config = BALANCE_CARD_CONFIG[label];
    const Icon = config?.icon;
    return (
      <div
        className={pageStyles.summaryCard}
        style={{
          borderLeft: config?.accent ? `4px solid ${config.accent}` : undefined,
          backgroundColor: config?.bgTint || '#fff',
        }}
      >
        <div className={pageStyles.summaryHeader}>
          {Icon && (
            <div
              aria-hidden
              className={pageStyles.summaryIcon}
              style={{ backgroundColor: config?.accent }}
            >
              <Icon sx={{ fontSize: 20 }} />
            </div>
          )}
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {label}
          </Typography>
        </div>
        <div>
          <Typography variant="body2" color="text.secondary">
            Used: {used ?? '—'}
          </Typography>
        </div>
        <Typography variant="body2" color="text.secondary">
          Remaining: {remaining ?? '—'}
        </Typography>
      </div>
    );
  };

  const orgOptions = [
    { label: 'Select organization', value: '' },
    ...(orgs || []).map((org) => ({ label: org.orgName || org.orgId, value: org.orgId })),
  ];

  const orgSelector = (
    <div className={usageToolbar.toolbarRow}>
      <div className={usageToolbar.toolbarIcon} aria-hidden>
        <BusinessIcon sx={{ fontSize: 20 }} />
      </div>
      <div className={usageToolbar.orgSelectShell}>
        <FormComponent
          type="select"
          label="Organization"
          name="usageByUserOrg"
          id="usage-by-user-org-select"
          value={selectedOrgId}
          onChange={handleOrgChange}
          options={orgOptions}
          size="small"
          disabled={loadingOrgs}
        />
      </div>
      <div className={usageToolbar.dateFieldShell}>
        <FormComponent
          type="date"
          id="usage-by-user-start-date"
          name="usageByUserStartDate"
          label="Start date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value || '')}
          size="small"
        />
      </div>
      <div className={usageToolbar.dateFieldShell}>
        <FormComponent
          type="date"
          id="usage-by-user-end-date"
          name="usageByUserEndDate"
          label="End date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value || '')}
          size="small"
        />
      </div>
      {loadingOrgs && (
        <div className={usageToolbar.toolbarLoading}>
          <CircularProgress size={18} />
          <Typography variant="caption" color="text.secondary">Loading…</Typography>
        </div>
      )}
    </div>
  );

  return (
    <Box className={styles['page-container']} sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexShrink: 0 }}>
        <PageHeader
          breadcrumbItems={breadcrumbItems}
          title="Usage by User"
          description="View per-user credit usage for the selected organization."
          actions={orgSelector}
        />
      </Box>

      <Box
        className={styles['cover-container-col']}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          width: '100%',
          px: 2,
          pt: 2,
          pb: 2,
        }}
      >
        {selectedOrgId && (
          <div className={pageStyles.layout}>
            {/* Left (20%): Organization summary */}
            <div className={pageStyles.leftPane}>
              <div className={pageStyles.card}>
                <h2 className={pageStyles.cardTitle}>Organization summary</h2>
                <div className={pageStyles.summaryList}>
                  <div className={pageStyles.summaryItem}>
                    <SummaryCard
                      label="Credits"
                      used={orgTotals.creditsUsed}
                      remaining={orgRemaining.creditsRemaining}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right (78%): Usage by user table */}
            <div className={pageStyles.rightPane}>
              <div className={`${pageStyles.card} ${pageStyles.tableCard}`}>
                <h2 className={pageStyles.cardTitle}>Usage by user</h2>
                <div className={pageStyles.tableRegion} role="region" aria-label="Usage by user table">
                <TableComponent
                  title=""
                  isHeaderContentVisible={false}
                  hideColumnsVisibilityButton
                  rows={userSummary}
                  columns={[
                    { field: 'userName', headerName: 'User', flex: 1, minWidth: 120, renderCell: (params) => renderTableCell(params.value || '—') },
                    { field: 'userEmail', headerName: 'Email', flex: 1, minWidth: 160, renderCell: (params) => renderTableCell(params.value || '—') },
                    { field: 'creditsUsed', headerName: 'Credits used', minWidth: 120, renderCell: (params) => renderTableCell(params.value ?? '—') },
                    {
                      field: 'Actions',
                      headerName: 'Actions',
                      minWidth: 130,
                      sortable: false,
                      filterable: false,
                      disableColumnMenu: true,
                      renderCell: (params) => (
                        <ButtonComponent
                          variant="outline"
                          className="primary"
                          size="small"
                          ctaText="View History"
                          startIcon={<HistoryIcon className="icon" />}
                          onClick={() => handleViewHistory(params.row)}
                          aria-label={`View history for ${params.row.userName || params.row.userEmail || 'user'}`}
                        />
                      ),
                    },
                  ]}
                  loading={loadingSummary}
                  disablePagination
                  disableFooter
                  idField="userId"
                  viewType="usageByUser"
                  customNoData={
                    <div className={pageStyles.noData}>
                      <Typography color="text.secondary" variant="body2">
                        No usage data for this organization.
                      </Typography>
                    </div>
                  }
                  isData={userSummary.length > 0 || loadingSummary}
                  tableHeight="100%"
                />
              </div>
              </div>
            </div>
          </div>
        )}

        {!selectedOrgId && !loadingOrgs && orgs.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            No organizations with plans found.
          </Typography>
        )}
        {!selectedOrgId && !loadingOrgs && orgs.length > 0 && (
          <Typography color="text.secondary" sx={{ py: 4 }}>
            Select an organization above to view usage by user.
          </Typography>
        )}
      </Box>

      <Dialog open={historyModalOpen} onClose={handleCloseHistoryModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          Usage history — {historyUser ? (historyUser.userName || historyUser.userEmail || 'Unknown user') : ''}
        </DialogTitle>
        <DialogContent dividers sx={{ width: '100%' }}>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : historyRows.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No history for this user in the selected date range.
            </Typography>
          ) : (
            <Box sx={{ height: 420, minHeight: 260, width: '100%' }}>
              <TableComponent
                title=""
                isHeaderContentVisible={false}
                hideColumnsVisibilityButton
                rows={historyRows}
                columns={[
                  { field: 'section', headerName: 'Section', minWidth: 130, flex: 0, renderCell: (params) => renderTableCell(SECTION_LABELS[params.value] || params.value) },
                  { field: 'amountUsed', headerName: 'Amount Used', minWidth: 120, flex: 0, renderCell: (params) => renderTableCell(params.value ?? '—') },
                  { field: 'usedAt', headerName: 'Used At', minWidth: 200, flex: 1, renderCell: (params) => renderTableCell(formatDate(params.value)) },
                ]}
                loading={loadingHistory}
                isPagination={true}
                paginationMode="server"
                rowCount={historyPagination.total || historyRows.length}
                paginationModel={historyPaginationModel}
                handlePaginationModelChange={handleHistoryPaginationModelChange}
                handleCustomPaginationPageChange={handleHistoryPageChange}
                handleCustomPaginationPageSizeChange={handleHistoryPageSizeChange}
                isCustomPagination={true}
                disableFooter={false}
                disablePagination={false}
                idField="id"
                viewType="usageHistoryModal"
                isData
                tableHeight="100%"
                customStyles={{ width: '100%', minWidth: '100%' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <ButtonComponent
            variant="outline"
            className="primary"
            ctaText="Close"
            onClick={handleCloseHistoryModal}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsageByUser;
